import { createContext, ReactNode, useState, useEffect } from 'react'

type LocationContextType = {
  latitude?: number
  longitude?: number
  browserLocationGranted: boolean
  requestBrowserLocation: () => void
  setLocation: (lat: number, lon: number) => void
}

export const LocationContext = createContext<LocationContextType>({
  latitude: undefined,
  longitude: undefined,
  browserLocationGranted: false,
  requestBrowserLocation: () => {
    throw new Error('requestLocation must be used within LocationContext')
  },
  setLocation: () => {
    throw new Error('setLocation must be used within LocationContext')
  }
})

export type LocationProviderProps = {
  children: ReactNode
}

/**
 * Use to provide the active TabContext to consumers. DefaultTab can be
 * supplied in order to set the tab initially rendered.
 */
const LocationProvider = ({ children }: LocationProviderProps) => {
  const [latitude, setLatitude] = useState<number | undefined>(undefined)
  const [longitude, setLongitude] = useState<number | undefined>(undefined)
  const [browserLocationGranted, setBrowserLocationGranted] =
    useState<boolean>(false)

  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then(status => {
        console.log('Geolocation permission status:', status.state)
        if (status.state === 'granted') {
          setBrowserLocationGranted(true)
          requestBrowserLocation()
        }
        status.addEventListener('change', () => {
          setBrowserLocationGranted(status.state === 'granted')
        })
      })
    }
  }, [])

  const requestBrowserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setLatitude(position.coords.latitude)
          setLongitude(position.coords.longitude)
        },
        error => {
          console.error('Error getting location:', error)
        }
      )
    } else {
      console.error('Geolocation is not supported by this browser.')
    }
  }

  const setLocation = (lat: number, lon: number) => {
    setLatitude(lat)
    setLongitude(lon)
  }

  return (
    <LocationContext.Provider
      value={{
        latitude,
        longitude,
        requestBrowserLocation,
        browserLocationGranted,
        setLocation
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}

export default LocationProvider
